import { z } from "zod";
import { ID_PATTERN, idSchema, singleLineTextSchema } from "./text.js";
// M6 R6, Task18–19, INGR-9/IMP-10: righe lette nel browser, validate anche dal server.
// Campi derivati, owner e status non sono colonne da sovrascrivere: decide il nucleo ingresso.
export const FILE_IMPORT_MAX_ROWS = 5_000;
export const FILE_IMPORT_CHUNK_ROWS = 500;
export const FILE_IMPORT_SYSTEM_FIELDS = [
    "mobile", "landline", "firstName", "lastName", "salonName", "email", "website", "streetAddress", "postalCode",
    "city", "cityIstatCode", "province", "secondHolderFirstName", "secondHolderLastName", "secondHolderEmail", "secondHolderMobile", "tags",
];
const fieldSchema = z.string().refine((value) => FILE_IMPORT_SYSTEM_FIELDS.includes(value) || (value.startsWith("custom:") && ID_PATTERN.test(value.slice(7))), { message: "Unknown import field" });
export const fileImportMappingSchema = z.record(fieldSchema, z.number().int().min(0))
    .refine((mapping) => mapping.mobile !== undefined || mapping.landline !== undefined, { message: "Map a primary phone column" })
    .refine((mapping) => new Set(Object.values(mapping)).size === Object.keys(mapping).length, { message: "Map each column once" })
    .refine((mapping) => mapping.province === undefined || mapping.city !== undefined, { message: "Province disambiguates a city name" })
    .refine((mapping) => mapping.city === undefined || mapping.cityIstatCode === undefined, { message: "Map the city by name or ISTAT code" });
export const startFileImportBodySchema = z.strictObject({
    requestId: idSchema,
    fileName: singleLineTextSchema(255),
    rowCount: z.number().int().min(1).max(FILE_IMPORT_MAX_ROWS),
    columnCount: z.number().int().min(1),
    mapping: fileImportMappingSchema,
    source: singleLineTextSchema(200),
    tagForAll: singleLineTextSchema(120).optional(),
    onExisting: z.enum(["keep", "fill_empty"]),
    assign: z.discriminatedUnion("mode", [
        z.strictObject({ mode: z.literal("rule") }),
        z.strictObject({ mode: z.literal("one"), membershipId: idSchema }),
    ]),
}).refine((config) => Object.values(config.mapping).every((index) => index < config.columnCount), { message: "Map a column in the file header", path: ["mapping"] });
export const fileImportRowsBodySchema = z.strictObject({
    offset: z.number().int().min(0).max(FILE_IMPORT_MAX_ROWS - 1),
    // Il corpo HTTP ha già il limite comune; i valori dei singoli campi li valida il servizio riga per riga.
    rows: z.array(z.array(z.string())).min(1).max(FILE_IMPORT_CHUNK_ROWS),
}).refine((chunk) => chunk.offset + chunk.rows.length <= FILE_IMPORT_MAX_ROWS, { message: "Rows exceed the file limit" });
/**
 * Perché una riga non è entrata, oltre ai rifiuti del writer della scheda: senza telefono, telefono che non si legge, valore non
 * valido di un Lead nuovo, numero di un Lead archiviato, fisso di un Lead con un altro cellulare (`phones_conflict`). Il server può
 * mandare anche altri codici di `LeadServiceError`: la finestra ha un testo generico per quelli che non conosce.
 */
export const FILE_IMPORT_PROBLEM_REASONS = [
    "phone_missing", "invalid_phone", "invalid_field_value", "archived_lead", "phones_conflict", "owner_not_eligible", "phone_in_use", "field_not_editable", "phone_repeated_on_lead",
];
/** Gli avvisi di una riga entrata: città sconosciuta, email anche su un altro Lead, una cella di un Lead già presente non scritta. */
export const FILE_IMPORT_WARNING_REASONS = ["unknown_city", "possible_duplicate_email", "value_not_imported"];
const countSchema = z.number().int().nonnegative();
// `duplicate`: le righe con lo stesso contenuto di un ingresso delle 24 ore prima (INGR-4, «Doppio invio»), che non entrano di nuovo;
// zero quando manca (un riepilogo di prima della revisione del 15/10/2026).
export const fileImportReportSchema = z.object({
    runId: idSchema,
    status: z.enum(["running", "completed", "failed", "aborted"]),
    counts: z.object({ processed: countSchema, created: countSchema, updated: countSchema, discarded: countSchema, duplicate: countSchema.default(0) }),
    problems: z.array(z.object({ row: z.number().int().min(2), reason: z.string(), field: z.string().optional() })),
    warnings: z.array(z.object({ row: z.number().int().min(2), reason: z.enum(FILE_IMPORT_WARNING_REASONS), field: z.string().optional() })),
});
//# sourceMappingURL=file-import.js.map